import React from "react"
import PropTypes from "prop-types"
import theme from "styles/theme"
import renderLinks from "lib/render-links"

const borderWidth = 2
const border = `${borderWidth}px solid ${theme.midBlue}`

const Table = ({ headings, rows, className, ...rest }, ref) => {
  const columnCount = rows.reduce(
    (max, row) => Math.max(max, row.cells.length),
    0
  )

  return (
    <table
      ref={ref}
      css={theme.expandQueries({
        width: "100%",
        borderSpacing: 0,
        color: theme.darkGray,
        fontSize: [14, 12, 11],
        lineHeight: 1.4,
        borderRight: "var(--inlinePadding) solid transparent",
        "--cellPadding": ["18px", "18px", "12px"],
        [["td", "th"]]: {
          height: [68, 58, 50], // acts as min-height on tables
          verticalAlign: "center",
          padding: "var(--cellPadding)",
          borderRight: border,
          borderBottom: border,
          textAlign: "center",
          wordBreak: "normal",
        },
        tbody: {
          [["td", "th"]]: {
            background: theme.white,
          },
        },
        a: {
          textDecoration: "underline",
          transition: "color 250ms",
          ":hover": {
            color: theme.midBlue,
          },
        },
      })}
      className={className}
      {...rest}
    >
      {headings && (
        <thead>
          <tr>
            {headings.map((heading, index) => (
              <th
                key={index}
                css={{
                  ...theme.tableHeader,
                  color: theme.midBlue,
                  background: theme.blue20,
                  borderTop: border,
                  ":first-of-type": {
                    borderLeft: border,
                  },
                }}
              >
                {heading}
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {rows.map(({ cells, mode }, rowIndex) => (
          <tr key={rowIndex}>
            {mode === "heading" ? (
              <th
                css={{
                  background: `${theme.lighterYellow} !important`,
                  fontWeight: 700,
                  textAlign: "left !important",
                  borderLeft: 0,
                  position: "relative !important",
                  boxShadow: "none !important",
                }}
                colSpan={columnCount}
              >
                <span
                  css={theme.expandQueries({
                    position: "sticky",
                    left: "var(--cellPadding)",
                    display: "block",
                    maxWidth: `calc(100vw - 2 * (${borderWidth}px + var(--inlinePadding) + var(--cellPadding)))`,
                    ":before": {
                      content: "''",
                      position: "absolute",
                      top: -30,
                      left: "calc(-1 * var(--cellPadding))",
                      width: borderWidth,
                      height: 200,
                      background: theme.midBlue,
                    },
                  })}
                >
                  {cells[0]}
                </span>
              </th>
            ) : (
              cells.map((cell, cellIndex) =>
                cellIndex === 0 ? (
                  <th
                    key={cellIndex}
                    css={{
                      background: theme.white,
                      textAlign: "left !important",
                      borderLeft: border,
                      fontWeight: 400,
                    }}
                  >
                    <Cell value={cell} />
                  </th>
                ) : (
                  <td key={cellIndex}>
                    <Cell value={cell} />
                  </td>
                )
              )
            )}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export default React.forwardRef(Table)

Table.propTypes = {
  headings: PropTypes.arrayOf(PropTypes.string).isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      cells: PropTypes.array.isRequired,
      mode: PropTypes.oneOf(["row", "heading"]),
    }).isRequired
  ).isRequired,
  className: PropTypes.string,
}

const Cell = ({ value }) => {
  if (typeof value === "undefined") {
    return null
  }

  const match = value.match(/{([+-])} *(.+)/)

  if (match) {
    const [, indicator, cellValue] = match
    const cellContent = renderLinks(cellValue)

    switch (indicator) {
      case "+":
        return <span css={{ color: "#43AE4B" }}>{cellContent}</span>
      case "-":
        return <span css={{ color: "#DF4700" }}>{cellContent}</span>
      default:
        return cellContent
    }
  }

  return renderLinks(value)
}
